// ============================================================
// Mini-blocs : objectifs de 10 games avec métrique mesurable
// ============================================================

// Templates prédéfinis
export const MINI_BLOCK_TEMPLATES = [
  {
    id: 'no-greed',
    title: 'Zéro mort greed',
    description: 'Maximum 2 morts greed au total sur 10 games',
    metric_type: 'total_greed_deaths',
    metric_operator: '<=',
    metric_target: 2,
    target_games: 10,
    difficulty: 'medium'
  },
  {
    id: 'avg-deaths',
    title: 'Discipline mort < 4',
    description: 'Moyenne < 4 morts/game sur 10 games',
    metric_type: 'avg_deaths',
    metric_operator: '<=',
    metric_target: 4,
    target_games: 10,
    difficulty: 'medium'
  },
  {
    id: 'winrate-60',
    title: 'Streak Challenger',
    description: 'Winrate ≥ 60% sur 10 games',
    metric_type: 'winrate',
    metric_operator: '>=',
    metric_target: 60,
    target_games: 10,
    difficulty: 'hard'
  },
  {
    id: 'clean-ratio',
    title: '80% games clean',
    description: '8 games sur 10 sans mort greed',
    metric_type: 'clean_ratio',
    metric_operator: '>=',
    metric_target: 80,
    target_games: 10,
    difficulty: 'medium'
  },
  {
    id: 'dpm-900',
    title: 'Damage output Master+',
    description: 'Dmg/min moyen ≥ 850 sur 10 games',
    metric_type: 'avg_dpm',
    metric_operator: '>=',
    metric_target: 850,
    target_games: 10,
    difficulty: 'hard'
  },
  {
    id: 'cs-9',
    title: 'Farm excellent',
    description: 'CS/min moyen ≥ 8.5 sur 10 games',
    metric_type: 'avg_cs',
    metric_operator: '>=',
    metric_target: 8.5,
    target_games: 10,
    difficulty: 'easy'
  },
  {
    id: 'cait-mastery',
    title: 'Maîtrise Caitlyn',
    description: '60%+ winrate sur 10 games Caitlyn',
    metric_type: 'winrate',
    metric_operator: '>=',
    metric_target: 60,
    target_games: 10,
    filter_champion: 'Caitlyn',
    difficulty: 'hard'
  },
  {
    id: 'kaisa-mastery',
    title: 'Maîtrise Kai\'Sa',
    description: '60%+ winrate sur 10 games Kai\'Sa',
    metric_type: 'winrate',
    metric_operator: '>=',
    metric_target: 60,
    target_games: 10,
    filter_champion: "Kai'Sa",
    difficulty: 'hard'
  }
];

export const METRIC_TYPES = {
  winrate: { label: 'Winrate', unit: '%', description: '% de victoires' },
  avg_deaths: { label: 'Morts/game moyen', unit: '', description: 'Morts moyennes par game' },
  total_greed_deaths: { label: 'Morts greed totales', unit: '', description: 'Nombre total de morts greed' },
  clean_ratio: { label: '% games clean', unit: '%', description: '% games sans mort greed' },
  avg_dpm: { label: 'Dmg/min moyen', unit: '', description: 'Damage/minute moyen' },
  avg_cs: { label: 'CS/min moyen', unit: '', description: 'CS/minute moyen' }
};

// Calcule la progression d'un mini-bloc depuis son démarrage
export function calculateBlockProgress(block, allGames) {
  // Games concernées : celles après le point de départ, éventuellement filtrées par champion
  let relevantGames = allGames.slice(block.started_at_game_count || 0);
  if (block.filter_champion) {
    relevantGames = relevantGames.filter(g => g.champion === block.filter_champion);
  }

  // Limite au nombre de games cibles
  const gamesInBlock = relevantGames.slice(0, block.target_games);
  const gamesPlayed = gamesInBlock.length;

  // Calcul de la métrique actuelle
  let currentValue = 0;
  if (gamesPlayed > 0) {
    switch (block.metric_type) {
      case 'winrate':
        currentValue = Math.round(gamesInBlock.filter(g => g.result === 'W').length / gamesPlayed * 100);
        break;
      case 'avg_deaths':
        currentValue = +(gamesInBlock.reduce((a, g) => a + g.deaths, 0) / gamesPlayed).toFixed(1);
        break;
      case 'total_greed_deaths':
        currentValue = gamesInBlock.reduce((a, g) => a + (g.deathDetails || []).filter(d => d.type === 'greed').length, 0);
        break;
      case 'clean_ratio':
        currentValue = Math.round(gamesInBlock.filter(g => !(g.deathDetails || []).some(d => d.type === 'greed')).length / gamesPlayed * 100);
        break;
      case 'avg_dpm':
        const gamesWithDmg = gamesInBlock.filter(g => g.damageDealt > 0 && g.duration > 0);
        currentValue = gamesWithDmg.length > 0 ? Math.round(gamesWithDmg.reduce((a, g) => a + (g.damageDealt / g.duration), 0) / gamesWithDmg.length) : 0;
        break;
      case 'avg_cs':
        const gamesWithCs = gamesInBlock.filter(g => g.csPerMin > 0);
        currentValue = gamesWithCs.length > 0 ? +(gamesWithCs.reduce((a, g) => a + g.csPerMin, 0) / gamesWithCs.length).toFixed(1) : 0;
        break;
    }
  }

  // Check si l'objectif est atteint (nécessite target_games games jouées pour valider)
  const isCompleted = gamesPlayed >= block.target_games && checkOperator(currentValue, block.metric_operator, block.metric_target);
  const isFailed = gamesPlayed >= block.target_games && !isCompleted;

  // Pour les métriques "negative" (ex: morts, greed), calculer différemment
  const isNegativeMetric = ['avg_deaths', 'total_greed_deaths'].includes(block.metric_type);

  // Estimation de tendance : est-ce qu'au rythme actuel on va valider ?
  let projectedSuccess = null;
  if (gamesPlayed > 0 && gamesPlayed < block.target_games) {
    projectedSuccess = checkOperator(currentValue, block.metric_operator, block.metric_target);
  }

  return {
    gamesPlayed,
    targetGames: block.target_games,
    progressPercent: Math.round(gamesPlayed / block.target_games * 100),
    currentValue,
    targetValue: block.metric_target,
    isCompleted,
    isFailed,
    projectedSuccess,
    isNegativeMetric
  };
}

function checkOperator(value, operator, target) {
  switch (operator) {
    case '>=': return value >= target;
    case '<=': return value <= target;
    case '>': return value > target;
    case '<': return value < target;
    case '=': return value === target;
    default: return false;
  }
}

// ============================================================
// V6.3 — Suggestion automatique de mini-blocs basée sur patterns récents
// ============================================================

/**
 * Analyse les 10-20 dernières games et suggère 3-5 objectifs intelligents
 * Chaque suggestion contient : titre, raison, métrique cible adaptée
 */
export function suggestMiniBlocks(games, existingBlocks = []) {
  const recentGames = games.slice(-20); // 20 dernières games
  if (recentGames.length < 5) return []; // pas assez de données

  const suggestions = [];

  // Calculs de stats récentes
  const totalGames = recentGames.length;
  const wins = recentGames.filter(g => g.result === 'W').length;
  const winrate = Math.round(wins / totalGames * 100);
  const avgDeaths = +(recentGames.reduce((s, g) => s + (g.deaths || 0), 0) / totalGames).toFixed(1);

  const allDeaths = recentGames.flatMap(g => g.deathDetails || []);
  const greedDeaths = allDeaths.filter(d => d.type === 'greed').length;
  const greedPct = allDeaths.length > 0 ? Math.round(greedDeaths / allDeaths.length * 100) : 0;

  const avgCs = +(recentGames.reduce((s, g) => s + (g.csPerMin || 0), 0) / totalGames).toFixed(1);

  // Si déjà un bloc actif sur la même métrique, on ne suggère pas de doublon
  const activeBlocks = existingBlocks.filter(b => b.status === 'active');
  const hasActiveOnMetric = (metric) => activeBlocks.some(b => b.metric_type === metric);

  // === Suggestion 1 : Réduire les morts greed totales (si > 5 récemment) ===
  if (greedDeaths > 5 && !hasActiveOnMetric('total_greed_deaths')) {
    const targetGreed = Math.max(3, Math.floor(greedDeaths / 2));
    suggestions.push({
      id: 'sug_greed',
      title: 'Discipliner les morts greed',
      description: `${greedPct}% de tes morts récentes sont greed. Cible : ≤ ${targetGreed} morts greed sur 10 games.`,
      reason: `${greedDeaths} morts greed sur ${allDeaths.length} morts récentes`,
      priority: 'high',
      metric_type: 'total_greed_deaths',
      metric_operator: '<=',
      metric_target: targetGreed,
      target_games: 10
    });
  }

  // === Suggestion 2 : Améliorer le winrate (si < 54%) ===
  if (winrate < 54 && !hasActiveOnMetric('winrate')) {
    const target = winrate < 45 ? 50 : 54;
    suggestions.push({
      id: 'sug_winrate',
      title: `Remonter le winrate à ${target}%`,
      description: `Winrate récent à ${winrate}%. Vise un climb sain à ${target}%+.`,
      reason: `${wins}W/${totalGames - wins}L sur tes ${totalGames} dernières games`,
      priority: winrate < 45 ? 'high' : 'medium',
      metric_type: 'winrate',
      metric_operator: '>=',
      metric_target: target,
      target_games: 10
    });
  }

  // === Suggestion 3 : Réduire le total de morts (si > 5/game) ===
  if (avgDeaths > 5 && !hasActiveOnMetric('avg_deaths')) {
    const targetDeaths = Math.max(3, Math.floor(avgDeaths) - 1);
    suggestions.push({
      id: 'sug_deaths',
      title: `Réduire à ${targetDeaths} morts par game`,
      description: `Tu meurs ${avgDeaths} fois/game en moyenne. Vise -1 à -2.`,
      reason: `Moyenne ${avgDeaths}/game sur ${totalGames} games`,
      priority: 'high',
      metric_type: 'avg_deaths',
      metric_operator: '<=',
      metric_target: targetDeaths,
      target_games: 10
    });
  }

  // === Suggestion 4 : Améliorer le CS/min (si < 7) ===
  if (avgCs < 7 && !hasActiveOnMetric('avg_cs')) {
    suggestions.push({
      id: 'sug_cs',
      title: 'Améliorer le farming',
      description: `CS/min à ${avgCs}. Master+ vise 7.5+.`,
      reason: `${avgCs} CS/min de moyenne sur ${totalGames} games`,
      priority: 'medium',
      metric_type: 'avg_cs',
      metric_operator: '>=',
      metric_target: 7.5,
      target_games: 10
    });
  }

  // === Suggestion 5 : Games clean (si pourcentage faible) ===
  const cleanGames = recentGames.filter(g => {
    const greed = (g.deathDetails || []).filter(d => d.type === 'greed').length;
    return greed === 0;
  }).length;
  const cleanPct = Math.round(cleanGames / totalGames * 100);
  if (cleanPct < 60 && !hasActiveOnMetric('clean_ratio')) {
    suggestions.push({
      id: 'sug_clean',
      title: 'Series de games clean',
      description: `Seulement ${cleanPct}% de tes games récentes sont sans mort greed. Vise 70%+.`,
      reason: `${cleanGames}/${totalGames} games clean récemment`,
      priority: 'medium',
      metric_type: 'clean_ratio',
      metric_operator: '>=',
      metric_target: 70,
      target_games: 10
    });
  }

  // Trier : priorité haute d'abord, puis medium, puis low
  const priorityWeight = { high: 3, medium: 2, low: 1 };
  suggestions.sort((a, b) => priorityWeight[b.priority] - priorityWeight[a.priority]);

  return suggestions.slice(0, 5);
}
